import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiCertificationAPI } from '../../services/api';
import { Plus, Search, Trash2, ClipboardCheck, ChevronLeft, ChevronRight, Download, Filter, Settings2, X, Upload, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AICertBulkImportModal from './AICertBulkImportModal';
import ImportFromAccountsModal from './ImportFromAccountsModal';

const ACTIVITY_CONFIGS = {
  kyc: { label: 'KYC', statuses: ['Complete', 'Pending'] },
  cdsl_link: { label: 'CDSL Link', statuses: ['Link sent to Investor', 'Investor has approved the login'] },
  ai_application: { label: 'AI Application', statuses: ['AI Application filed with Agency', 'Application Approved'] },
  ai_certificate: { label: 'AI Certificate', statuses: ['Certificate Generated', 'Certificate Received'] },
  invoicing: { label: 'Invoicing', statuses: ['Invoice raised and sent to Investor', 'Invoice Paid'] },
};

const STATUS_COLORS = {
  'Complete': 'bg-green-100 text-green-700 border-green-200',
  'Pending': 'bg-amber-100 text-amber-700 border-amber-200',
  'Link sent to Investor': 'bg-blue-100 text-blue-700 border-blue-200',
  'Investor has approved the login': 'bg-green-100 text-green-700 border-green-200',
  'AI Application filed with Agency': 'bg-blue-100 text-blue-700 border-blue-200',
  'Application Approved': 'bg-green-100 text-green-700 border-green-200',
  'Certificate Generated': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Certificate Received': 'bg-green-100 text-green-700 border-green-200',
  'Invoice raised and sent to Investor': 'bg-orange-100 text-orange-700 border-orange-200',
  'Invoice Paid': 'bg-green-100 text-green-700 border-green-200',
};

const StatusBadge = ({ activity, record }) => {
  const activityData = record[activity];
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef(null);

  if (!activityData || !activityData.status) {
    return <span className="text-xs text-gray-300 italic">--</span>;
  }

  const { status, remarks, days_since } = activityData;
  const colorClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  const showDays = days_since !== undefined && days_since !== null;
  const hasPendingRemarks = status === 'Pending' && remarks;

  return (
    <div className="relative inline-block" ref={badgeRef}>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-default ${colorClass}`}
        onMouseEnter={() => hasPendingRemarks && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {status.length > 18 ? status.substring(0, 18) + '...' : status}
        {showDays && <span className="ml-1 text-[10px] opacity-70">({days_since}d)</span>}
      </span>
      {showTooltip && hasPendingRemarks && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
          <div className="font-semibold mb-1">Pending Data:</div>
          <div className="whitespace-pre-wrap">{remarks}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
};

const AICertifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [investorTypes, setInvestorTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [canExport, setCanExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportSettings, setShowExportSettings] = useState(false);
  const [exportRoles, setExportRoles] = useState(['admin']);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showImportAccounts, setShowImportAccounts] = useState(false);

  // Activity filters
  const [activityFilters, setActivityFilters] = useState({
    kyc_status: '', cdsl_status: '', ai_app_status: '', ai_cert_status: '', invoicing_status: ''
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';
  const ep = user?.effective_permissions || user?.module_permissions || {};
  const hasInvestorAccounts = ep.investor_accounts === true;

  const activeFilterCount = Object.values(activityFilters).filter(Boolean).length;

  useEffect(() => {
    loadData();
  }, [page, pageSize, typeFilter, activityFilters]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page === 1) loadData();
      else setPage(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  useEffect(() => {
    checkExportPermission();
  }, []);

  const checkExportPermission = async () => {
    try {
      const res = await aiCertificationAPI.getExportRoles();
      const roles = res.data.roles || ['admin'];
      setExportRoles(roles);
      setCanExport(roles.includes(user?.role));
    } catch {
      setCanExport(user?.role === 'admin');
    }
  };

  const loadData = async () => {
    try {
      const [listRes, typesRes] = await Promise.all([
        aiCertificationAPI.getAll({
          search: searchTerm, investor_type: typeFilter, page, page_size: pageSize,
          ...activityFilters
        }),
        aiCertificationAPI.getInvestorTypes(),
      ]);
      setRecords(listRes.data.data);
      setTotalRecords(listRes.data.total);
      setTotalPages(listRes.data.total_pages);
      setInvestorTypes(typesRes.data.all_types || []);
    } catch (err) {
      console.error('Failed to load AI certifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this record?')) return;
    try {
      await aiCertificationAPI.delete(id);
      setRecords(prev => prev.filter(r => r.id !== id));
      setTotalRecords(prev => prev - 1);
    } catch { alert('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected records?`)) return;
    try {
      await aiCertificationAPI.bulkDelete([...selected]);
      setSelected(new Set());
      loadData();
    } catch { alert('Failed to delete'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === records.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(records.map(r => r.id)));
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await aiCertificationAPI.exportData(format, {
        search: searchTerm, investor_type: typeFilter, ...activityFilters
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'excel' ? 'AI_Certification_Tracker.xlsx' : 'AI_Certification_Tracker.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.status === 403 ? 'You don\'t have export permission' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveExportRoles = async (roles) => {
    try {
      const res = await aiCertificationAPI.updateExportRoles(roles);
      setExportRoles(res.data.roles);
      setCanExport(res.data.roles.includes(user?.role));
      setShowExportSettings(false);
    } catch { alert('Failed to update'); }
  };

  const setActivityFilter = (key, value) => {
    setActivityFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setActivityFilters({ kyc_status: '', cdsl_status: '', ai_app_status: '', ai_cert_status: '', invoicing_status: '' });
    setTypeFilter('');
    setPage(1);
  };

  // Completion summary
  const completedCount = records.filter(r =>
    r.kyc?.status === 'Complete' &&
    r.cdsl_link?.status === 'Investor has approved the login' &&
    r.ai_application?.status === 'Application Approved' &&
    r.ai_certificate?.status === 'Certificate Received' &&
    r.invoicing?.status === 'Invoice Paid'
  ).length;

  const inProgressCount = records.filter(r =>
    (r.kyc?.status || r.cdsl_link?.status || r.ai_application?.status || r.ai_certificate?.status || r.invoicing?.status) &&
    !(r.kyc?.status === 'Complete' &&
      r.cdsl_link?.status === 'Investor has approved the login' &&
      r.ai_application?.status === 'Application Approved' &&
      r.ai_certificate?.status === 'Certificate Received' &&
      r.invoicing?.status === 'Invoice Paid')
  ).length;

  if (loading) return <div className="p-8 text-center text-gray-500">Loading AI certifications...</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="ai-certifications-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Accredited Investor Certification</h1>
          <p className="text-sm text-gray-500 mt-1">Track AI certification process for each investor</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && selected.size > 0 && (
            <button onClick={handleBulkDelete} className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="bulk-delete-btn">
              <Trash2 size={14} className="mr-1" /> Delete ({selected.size})
            </button>
          )}
          {canExport && (
            <div className="relative group">
              <button disabled={exporting} className="inline-flex items-center px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="export-btn">
                <Download size={14} className="mr-1" /> {exporting ? 'Exporting...' : 'Export'}
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                <button onClick={() => handleExport('excel')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg" data-testid="export-excel-btn">Export as Excel</button>
                <button onClick={() => handleExport('pdf')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-b-lg" data-testid="export-pdf-btn">Export as PDF</button>
              </div>
            </div>
          )}
          {isAdmin && (
            <button onClick={() => setShowExportSettings(true)} className="p-2 border rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50" title="Export permissions" data-testid="export-settings-btn">
              <Settings2 size={16} />
            </button>
          )}
          {canEdit && (
            <button onClick={() => setShowBulkImport(true)} className="inline-flex items-center px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="bulk-import-btn">
              <Upload size={14} className="mr-1.5" /> Bulk Upload
            </button>
          )}
          {canEdit && hasInvestorAccounts && (
            <button onClick={() => setShowImportAccounts(true)} className="inline-flex items-center px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="import-accounts-btn">
              <UserPlus size={14} className="mr-1.5" /> Import from Accounts
            </button>
          )}
          {canEdit && (
            <button onClick={() => navigate('/ai-certification/create')} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="create-certification-btn">
              <Plus size={16} className="mr-2" /> Add Investor
            </button>
          )}
        </div>
      </div>

      {/* Search + Basic Filters */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name, PAN, email or mobile..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="search-input" />
        </div>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">All Types</option>
          {investorTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm ${activeFilterCount > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
          data-testid="toggle-activity-filters"
        >
          <Filter size={14} className="mr-1.5" />
          Activity Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
        </button>
        {activeFilterCount > 0 && (
          <button onClick={clearAllFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-700" data-testid="clear-all-filters">
            <X size={14} className="mr-1" /> Clear All
          </button>
        )}
        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm" data-testid="page-size-select">
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>

      {/* Activity Status Filters (collapsible) */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 border rounded-lg" data-testid="activity-filters-panel">
          {Object.entries(ACTIVITY_CONFIGS).map(([key, cfg]) => {
            const filterKey = key === 'ai_application' ? 'ai_app_status' : key === 'ai_certificate' ? 'ai_cert_status' : key === 'cdsl_link' ? 'cdsl_status' : key === 'invoicing' ? 'invoicing_status' : 'kyc_status';
            return (
              <select
                key={key}
                value={activityFilters[filterKey]}
                onChange={e => setActivityFilter(filterKey, e.target.value)}
                className={`border rounded-lg px-3 py-2 text-sm ${activityFilters[filterKey] ? 'border-blue-400 bg-blue-50' : ''}`}
                data-testid={`filter-${key}`}
              >
                <option value="">{cfg.label}: All</option>
                {cfg.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                <option value="__none__">Not Set</option>
              </select>
            );
          })}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Investors</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="total-count">{totalRecords}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Fully Certified</p>
          <p className="text-2xl font-bold text-green-600 mt-1" data-testid="certified-count">{completedCount}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">In Progress</p>
          <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="in-progress-count">{inProgressCount}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Not Started</p>
          <p className="text-2xl font-bold text-gray-400 mt-1" data-testid="not-started-count">{totalRecords - completedCount - inProgressCount}</p>
        </div>
      </div>

      {/* Table */}
      {records.length === 0 ? (
        <div className="text-center py-12 bg-white border rounded-lg">
          <ClipboardCheck size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">{activeFilterCount > 0 ? 'No records match the selected filters.' : 'No AI certification records found.'}</p>
          {activeFilterCount > 0 && <button onClick={clearAllFilters} className="mt-3 text-sm text-blue-600 hover:underline">Clear filters</button>}
          {!activeFilterCount && canEdit && <button onClick={() => navigate('/ai-certification/create')} className="mt-3 text-sm text-blue-600 hover:underline">Add your first investor</button>}
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="certifications-table">
            <thead>
              <tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                {isAdmin && (
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={selected.size === records.length && records.length > 0} onChange={toggleSelectAll} className="rounded" data-testid="select-all-checkbox" />
                  </th>
                )}
                <th className="px-4 py-3">Investor Name</th>
                <th className="px-4 py-3">PAN</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-3 py-3 text-center">Resident</th>
                <th className="px-3 py-3 text-center">KYC</th>
                <th className="px-3 py-3 text-center">CDSL Link</th>
                <th className="px-3 py-3 text-center">AI Application</th>
                <th className="px-3 py-3 text-center">AI Certificate</th>
                <th className="px-3 py-3 text-center">Invoicing</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/ai-certification/${r.id}`)} data-testid={`cert-row-${r.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="rounded" />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-gray-900">{r.investor_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.pan || '--'}</td>
                  <td className="px-4 py-3 text-gray-600">{r.investor_type}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${r.is_resident ? 'bg-teal-50 text-teal-700' : 'bg-orange-50 text-orange-700'}`}>
                      {r.is_resident ? 'Resident' : 'NRI'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center"><StatusBadge activity="kyc" record={r} /></td>
                  <td className="px-3 py-3 text-center"><StatusBadge activity="cdsl_link" record={r} /></td>
                  <td className="px-3 py-3 text-center"><StatusBadge activity="ai_application" record={r} /></td>
                  <td className="px-3 py-3 text-center"><StatusBadge activity="ai_certificate" record={r} /></td>
                  <td className="px-3 py-3 text-center"><StatusBadge activity="invoicing" record={r} /></td>
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    {canEdit && <button onClick={e => handleDelete(r.id, e)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-btn-${r.id}`}><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <span>Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalRecords)} of {totalRecords}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50" data-testid="prev-page-btn">
              <ChevronLeft size={16} />
            </button>
            <span className="px-2">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50" data-testid="next-page-btn">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Export Settings Modal (Admin only) */}
      {showExportSettings && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowExportSettings(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="export-settings-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Export Permissions</h3>
            <p className="text-sm text-gray-500 mb-4">Select which roles can export data from this module.</p>
            <div className="space-y-3">
              {['admin', 'manager', 'viewer'].map(role => (
                <label key={role} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportRoles.includes(role)}
                    disabled={role === 'admin'}
                    onChange={e => {
                      if (role === 'admin') return;
                      const next = e.target.checked ? [...exportRoles, role] : exportRoles.filter(r => r !== role);
                      setExportRoles(next);
                    }}
                    className="rounded text-blue-600"
                    data-testid={`export-role-${role}`}
                  />
                  <span className="text-sm text-gray-800 capitalize">{role}</span>
                  {role === 'admin' && <span className="text-xs text-gray-400">(always enabled)</span>}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowExportSettings(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleSaveExportRoles(exportRoles)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-export-roles-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <AICertBulkImportModal isOpen={showBulkImport} onClose={() => setShowBulkImport(false)} onSuccess={loadData} />

      {/* Import from Accounts Modal */}
      <ImportFromAccountsModal isOpen={showImportAccounts} onClose={() => setShowImportAccounts(false)} onSuccess={loadData} />
    </div>
  );
};

export default AICertifications;
